import { CrudStoreItem } from 'legacy/lut/classes/models/CrudStoreItem'
import { DataModel } from 'legacy/lut/classes/models/DataModel'
import { RequestTypes } from 'legacy/lut/classes/models/onboarding/Onboarding'
import { PartnersFilterArea } from 'legacy/lut/components/widgets/public/onboarding/filtersArea/PartnersFilterArea'
import { OnbPartnerFormDialog } from 'legacy/lut/components/widgets/public/onboarding/onbPartnerFormDialog/OnbPartnerFormDialog'
import { PartnerTable } from 'legacy/lut/components/widgets/public/partnerTable/PartnerTable'
import React, { useMemo, useState } from 'react'

import { IColumn } from 'core-components/library/list/types'
import { LoaderOverlay } from 'core-components/library/loader'
import { Logic, Operator } from 'core-components/types'
import { IDataProviderFilter } from 'core-components/types'
import { CrudStore } from 'core/types/crudStore'

export interface ISelectItem {
  CommunicationName: string
  PartnerID: string
  LocationID: string
  PartnerType: string
  Region: string
  Country: string
  CC: string
  en: string
  Market: string
}

interface IProps {
  onlyNew?: boolean
  onboardingTable?: boolean
  onSelect: (item: ISelectItem) => void
  canSelectRule?: (item: DataModel) => boolean
  sourceKey: string
  sourceId?: CrudStoreItem
  filter?: IDataProviderFilter
  defaultFilter?: IDataProviderFilter
  disableEditAction?: boolean
  usePropsFilterOnly?: boolean
  showFilterArea?: boolean
  hasLoader?: boolean
  isLoading?: boolean
  columnsOptions?: Record<string, Partial<IColumn>>
}

// todo: refactor: pass complete source and columns
export const SelectPartnerTable = ({
  onboardingTable,
  onlyNew,
  onSelect,
  canSelectRule,
  sourceKey,
  sourceId,
  filter,
  defaultFilter,
  disableEditAction,
  usePropsFilterOnly,
  showFilterArea = true,
  hasLoader = false,
  isLoading: isLoadingProps,
  columnsOptions,
}: IProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const showLoader = () => hasLoader && setIsLoading(true)
  const hideLoader = () => hasLoader && setIsLoading(false)

  const [partner, setPartner] = useState<DataModel | undefined>(undefined)
  const [showPartnerDialog, setShowPartnerDialog] = useState(false)
  const crudCountry = CrudStore.get(CrudStoreItem.CountriesTable, 'Find Country')
  const partnersSource = useMemo(() => {
    if (!onlyNew) {
      const crud = CrudStore.get(sourceId || CrudStoreItem.PartnerAllData, sourceKey)
      if (usePropsFilterOnly) {
        if (defaultFilter) {
          crud.setDefaultFilter(defaultFilter)
        }
        if (filter) {
          crud.setFilter(filter)
        }

        return crud
      }
      if (sourceKey === RequestTypes.Onboarding) {
        crud.setDefaultFilter({
          logic: Logic.AND,
          filters: [
            {
              field: 'HierarchyType',
              operator: Operator.EQUAL,
              value: 'Headquarters',
            },
          ],
        })
      }
      if (filter) {
        crud.setFilter(filter)
      }

      return crud
    }

    const crud = CrudStore.get(CrudStoreItem.NewPartners, sourceKey)
    if (usePropsFilterOnly) {
      if (defaultFilter) {
        crud.setDefaultFilter(defaultFilter)
      }
      if (filter) {
        crud.setFilter(filter)
      }

      return crud
    }

    crud.setSort({ field: 'EntryId', dir: 'desc' })
    crud.setDefaultFilter({
      logic: Logic.AND,
      filters: [
        {
          field: 'PartnerID',
          operator: Operator.CONTAINS,
          value: 'new-',
        },
      ],
    })

    return crud
  }, [defaultFilter, filter, onlyNew, sourceId, sourceKey, usePropsFilterOnly])

  const columns = useMemo(() => {
    const notLegalFields: IColumn[] = [
      {
        field: 'PartnerType',
        title: 'Partner Type',
        priority: 0,
      },
      {
        field: 'HierarchyType',
        title: 'Hierarchy Type',
        priority: 1,
      },
      {
        field: 'CommunicationName',
        title: 'Comm. Name',
        priority: 2,
      },
      { field: 'PartnerID', title: 'Partner ID', priority: 3 },
      {
        field: 'ParentPartnerID',
        title: 'Parent Partner ID',
        priority: 4,
      },
      { field: 'Country', title: 'Country', priority: 5, ...(columnsOptions?.['Country'] ?? {}) },
      { field: 'LocationID', title: 'Location ID', priority: 6 },
      { field: 'City', title: 'City', priority: 8 },
      {
        field: 'CompanyLegalName',
        title: 'Legal Name',
        priority: 9,
      },
      { field: 'TaxID', title: 'Tax ID', priority: 10 },
      { field: 'Region', title: 'Region', priority: 16 },
    ]

    const onlyNewFields: IColumn[] = [
      { field: 'PartnerID', title: 'PartnerID', priority: 0 },
      { field: 'LocationID', title: 'LocationID', priority: 1 },
      { field: 'TaxID', title: 'TaxID', priority: 2 },
      { field: 'CommunicationName', title: 'CommunicationName', priority: 3 },
      { field: 'CompanyLegalName', title: 'CompanyLegalName', priority: 4 },
      { field: 'Region', title: 'Region', priority: 10 },
      { field: 'City', title: 'City', priority: 12 },
      { field: 'AddressLine1', title: 'AddressLine1', priority: 6 },
    ]

    const notOnbTitles: IColumn[] = [
      { field: 'MainPhone', title: 'Phone', priority: 7 },
      { field: 'ZIPCode', title: 'ZIPCode', priority: 11 },
      {
        field: 'AddressLine1',
        title: 'Address Line 1',
        priority: 12,
      },
      {
        field: 'AddressLine2',
        title: 'Address Line 2',
        priority: 13,
      },
      {
        field: 'AddressLine3',
        title: 'Address Line 3',
        priority: 14,
      },
      { field: 'State', title: 'State', priority: 15 },
    ]

    const notLegalColumns = notLegalFields.concat(
      !onboardingTable
        ? notOnbTitles
        : [
            {
              field: 'SelectPartner',
              title: '',
              priority: 17,
              hideSettings: true,
            },
          ]
    )

    const onlyNewColumns = onlyNewFields.concat(
      !onboardingTable
        ? notOnbTitles
        : [
            {
              field: 'SelectPartner',
              title: '',
              priority: 16,
              hideSettings: true,
            },
          ]
    )

    if (onlyNew) {
      return onlyNewColumns
    }
    return notLegalColumns
  }, [onboardingTable, onlyNew, columnsOptions])

  const selectCC = async (item: any) => {
    return crudCountry.getById({ id: item.get('Country'), keyField: 'en' })
  }

  return (
    <>
      {hasLoader || isLoadingProps ? (
        <LoaderOverlay show={isLoading || Boolean(isLoadingProps)} />
      ) : null}
      {showFilterArea ? <PartnersFilterArea source={partnersSource} /> : null}
      <PartnerTable
        onboardingTable={onboardingTable}
        className={'exist-part-tbl'}
        crudSource={partnersSource}
        columns={columns}
        disableExport={true}
        disableEditAction={disableEditAction}
        openPartnerDialog={(item) => {
          setPartner(item)
        }}
        onEdit={(item) => {
          setPartner(item)
          setShowPartnerDialog(true)
        }}
        onSelect={async (item) => {
          showLoader()
          const CC = await selectCC(item)
          hideLoader()

          onSelect({
            CommunicationName: item.get('CommunicationName'),
            PartnerID: item.get('PartnerID'),
            LocationID: item.get('LocationID'),
            Region: item.get('Region'),
            Country: item.get('Country'),
            PartnerType: item.get('PartnerType'),
            Market: item.get('Market'),
            CC: CC ? CC['CC'] : '',
            en: item.get('Country'),
          })
        }}
        canSelectRule={canSelectRule}
      />
      <OnbPartnerFormDialog
        show={showPartnerDialog}
        onClose={() => setShowPartnerDialog(false)}
        partnerId={partner?.get('PartnerID') || ''}
        appSource={sourceKey !== 'Onboarding' ? `Onboarding ${sourceKey}` : sourceKey}
      />
    </>
  )
}
